"use client";

import React from "react";
import { Wind, ThermometerSun, Snowflake, CircleCheck, ShieldCheck, ArrowRight, Wrench } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const services = [{
  title: "Redovisning",
  description: "Vi tar hand om din löpande bokföring, bokslut och deklarationer med precision och logik. Vi säkerställer att din ekonomiska administration är korrekt och levereras i tid.",
  icon: Wind,
  features: ["Löpande bokföring & rapportering", "Årsbokslut & inkomstdeklaration", "Moms- och skattehantering"]
}, {
  title: "Lön & HR",
  description: "Säkerställ korrekta löneutbetalningar och följsamhet mot lagar och avtal. Vi hanterar hela lönekedjan och stöttar dig i personalrelaterade frågor och administration.",
  icon: ThermometerSun,
  features: ["Komplett löneadministration", "Arbetsgivardeklarationer & kontrolluppgifter", "Rådgivning kring kollektivavtal"]
}, {
  title: "Rådgivning",
  description: "Strategiskt beslutsstöd för din verksamhetsutveckling. Vi hjälper dig att tolka de ekonomiska rapporterna och identifiera möjligheter för ökad lönsamhet och tillväxt.",
  icon: Snowflake,
  features: ["Kassaflödesanalys & budgetering", "Affärsutveckling & strategi", "Ekonomisk styrning & nyckeltal"]
}];
const warrantyDetails = [{
  question: "Hur säkerställer ni kvaliteten i ert arbete?",
  answer: "Vi arbetar enligt Reko, svensk standard för redovisningstjänster. Det innebär att vi följer strikta rutiner för dokumentation, kontroller och kompetensutveckling för att garantera att ditt företags ekonomi hanteras professionellt."
}, {
  question: "Är ni auktoriserade redovisningskonsulter?",
  answer: "Ja, vi värdesätter trygghet och expertis. Våra konsulter är antingen auktoriserade eller arbetar under ledning av auktoriserade medlemmar i branschorganisationer, vilket innebär en kvalitetsstämpel för dig som kund."
}, {
  question: "Erbjuder ni fasta priser eller löpande räkning?",
  answer: "Vi strävar efter transparens och förutsägbarhet. Beroende på ditt företags behov erbjuder vi både fasta månadspriser för paketerade tjänster och löpande debitering för mer specifika rådgivningsprojekt."
}, {
  question: "Hur ser uppstartsprocessen ut för en ny kund?",
  answer: "Det börjar med en behovsanalys där vi går igenom era nuvarande rutiner. Därefter sätter vi upp en gemensam arbetsplan och digitalt arbetsflöde. Vi ser till att övergången blir smidig genom att hantera all kontakt med er tidigare byrå eller Skatteverket."
}];
export default function OurCompleteRange() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: gridRef,
    isVisible: gridVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: warrantyRef,
    isVisible: warrantyVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="86361" id="våra-specialistområden" className="section bg-background relative overflow-hidden text-foreground">
      {}
      <div className="absolute inset-0 pointer-events-none opacity-[0.02] dark:opacity-[0.05]" style={{
      backgroundImage: 'radial-gradient(circle at 2px 2px, var(--color-primary) 1px, transparent 0)',
      backgroundSize: '48px 48px'
    }} />
      
      {}
      <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-primary/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3 pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        
        {}
        <div ref={headerRef} className={`max-w-3xl mx-auto text-center mb-16 transition-all duration-700 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-sm bg-primary/5 text-primary label font-semibold uppercase tracking-widest mb-6">
            <Wrench className="w-3.5 h-3.5" />
            <span>Specialistområden</span>
          </div>
          <h2 className="h2 text-primary font-semibold tracking-tight mb-6">
            Vårt kompletta utbud
          </h2>
          <p className="body text-muted-foreground mb-8">
            Vi levererar högkvalitativa ekonomitjänster med fokus på precision, personlig service och digital effektivitet. 
            Här är våra kärnområden där vi skapar värde för din verksamhet varje dag.
          </p>
        </div>

        {}
        <div ref={gridRef} className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-24">
          {services.map((service, i) => {
          const Icon = service.icon;
          return <div key={i} data-index={i} className={`group relative card bg-card p-8 transition-all duration-500 flex flex-col h-full ${gridVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`} style={{
            transitionDelay: `${i * 150}ms`
          }}>
                {}
                <div className="absolute inset-x-0 top-0 h-0.5 bg-gradient-to-r from-primary/20 to-primary/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-t-sm" />
                
                <div className="w-14 h-14 rounded-sm bg-primary/5 flex items-center justify-center mb-6 group-hover:bg-primary/10 transition-colors duration-300">
                  <Icon className="w-6 h-6 text-primary" />
                </div>
                
                <h3 className="h3 text-primary font-semibold mb-4">
                  {service.title}
                </h3>
                
                <p className="body text-muted-foreground mb-8 flex-grow">
                  {service.description}
                </p>
                
                <ul className="space-y-4 mb-8">
                  {service.features.map((feature, j) => <li key={j} data-index={j} className="flex items-start gap-3 text-sm text-foreground/90 font-medium">
                      <CircleCheck className="w-4 h-4 text-accent shrink-0 mt-0.5" />
                      <span>{feature}</span>
                    </li>)}
                </ul>
                
                <div className="mt-auto pt-6 border-t border-border/30">
                  <Button variant="ghost" className="w-full justify-between group/btn hover:bg-primary/5 text-primary font-semibold" asChild>
                    <Link to="/OurOfferPage">
                      Läs mer om {service.title.toLowerCase()}
                      <ArrowRight className="w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
                    </Link>
                  </Button>
                </div>
              </div>;
        })}
        </div>

        {}
        <div ref={warrantyRef} className={`max-w-4xl mx-auto card bg-muted/30 p-8 md:p-12 transition-all duration-700 ease-out ${warrantyVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="flex flex-col md:flex-row gap-12 items-start">
            <div className="md:w-1/3">
              <div className="w-12 h-12 rounded-sm bg-primary/5 flex items-center justify-center mb-6">
                <ShieldCheck className="w-6 h-6 text-primary" />
              </div>
              <h3 className="h3 text-primary font-semibold mb-4">
                Trygghet & Kvalitet
              </h3>
              <p className="body text-muted-foreground mb-6">
                Som din partner inom ekonomi är transparens och noggrannhet våra viktigaste ledord. Vi arbetar nära dig för att säkerställa högsta kvalitet i varje detalj av samarbetet.
              </p>
              <Button size="lg" className="w-full sm:w-auto bg-accent hover:bg-accent/90 text-accent-foreground font-semibold" asChild>
                <Link to="/ContactPage">
                  Få kostnadsfri offert
                </Link>
              </Button>
            </div>
            
            <div className="md:w-2/3 w-full">
              <Accordion type="single" collapsible className="w-full">
                {warrantyDetails.map((item, i) => <AccordionItem key={i} value={`item-${i}`} data-index={i} className="border-border/40">
                    <AccordionTrigger className="text-base font-semibold text-primary hover:text-accent hover:no-underline py-4">
                      {item.question}
                    </AccordionTrigger>
                    <AccordionContent className="body text-muted-foreground pb-6">
                      {item.answer}
                    </AccordionContent>
                  </AccordionItem>)}
              </Accordion>
            </div>
          </div>
        </div>

      </div>
    </section>;
}